"use client";

import Image from "next/image";
import { m } from "motion/react";
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  Flag,
  Send,
  X,
  Grid3X3,
  AlertTriangle,
} from "lucide-react";
import type { DemoSection, DemoQuestion } from "@/types/demo";
import { DEMO_SECTIONS } from "@/app/coba/_components/demo-data";
import { fmtTime } from "@/app/coba/_components/utils";
import { DemoModal } from "@/app/coba/_components/demo-modal";
import { Button } from "@/components/ui/button";

export interface ExamContentProps {
  // state values
  isWindowBlurred: boolean;
  penaltyFlash: boolean;
  penaltyMessage: string;
  timer: number;
  isUrgent: boolean;
  showNav: boolean;
  tabWarning: boolean;
  tabSwitchCount: number;
  showFullscreenWarning: boolean;
  showSubmitConfirm: boolean;
  answers: Record<string, string>;
  flags: Set<string>;
  sectionIdx: number;
  questionIdx: number;
  // derived values
  section: DemoSection;
  question: DemoQuestion;
  globalIdx: number;
  isFirst: boolean;
  isLast: boolean;
  answeredCount: number;
  totalQuestions: number;
  // refs
  demoMainRef: React.RefObject<HTMLElement | null>;
  // setters
  setShowSubmitConfirm: (v: boolean) => void;
  setShowNav: (v: boolean) => void;
  setTabWarning: (v: boolean) => void;
  setShowFullscreenWarning: (v: boolean) => void;
  // handlers
  toggleFlag: () => void;
  selectOption: (key: string) => void;
  goPrev: () => void;
  goNext: () => void;
  goTo: (si: number, qi: number) => void;
  getGlobalIdx: (si: number, qi: number) => number;
  requestFullscreen: () => void;
  handleSubmit: () => void;
}

export function ExamContent({
  isWindowBlurred,
  penaltyFlash,
  penaltyMessage,
  timer,
  isUrgent,
  showNav,
  tabWarning,
  tabSwitchCount,
  showFullscreenWarning,
  showSubmitConfirm,
  answers,
  flags,
  sectionIdx,
  questionIdx,
  section,
  question,
  globalIdx,
  isFirst,
  isLast,
  answeredCount,
  totalQuestions,
  demoMainRef,
  setShowSubmitConfirm,
  setShowNav,
  setTabWarning,
  setShowFullscreenWarning,
  toggleFlag,
  selectOption,
  goPrev,
  goNext,
  goTo,
  getGlobalIdx,
  requestFullscreen,
  handleSubmit,
}: ExamContentProps) {
  return (
    <div
      className="bg-background fixed inset-0 z-[100] flex h-dvh flex-col overflow-hidden select-none"
      style={
        isWindowBlurred
          ? { filter: "blur(30px)", pointerEvents: "none", userSelect: "none" }
          : undefined
      }
      onCopy={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
    >
      {/* Penalty flash */}
      {penaltyFlash && (
        <div className="bg-destructive text-primary-foreground absolute top-16 left-1/2 z-[70] -translate-x-1/2 animate-bounce rounded-lg px-4 py-2 text-sm font-semibold shadow-lg">
          {penaltyMessage}
        </div>
      )}

      {/* Header */}
      <header className="border-border/60 bg-card shadow-soft flex shrink-0 items-center justify-between border-b px-3 py-2 [--soft-shadow-color:var(--border)] sm:px-5 sm:py-2.5">
        <div />
        <m.div
          layout
          className={`flex items-center gap-1.5 rounded-xl border-2 px-3 py-2 font-mono sm:gap-2 sm:px-5 sm:py-2.5 ${isUrgent ? "border-destructive/30 bg-destructive" : "border-foreground/10 bg-foreground"}`}
        >
          <Clock size={14} className="text-background/50 sm:size-4" />
          {isUrgent ? (
            <m.span
              animate={{ scale: [1, 1.08, 1], opacity: [1, 0.5, 1] }}
              transition={{ repeat: Infinity, duration: 0.8 }}
              className="text-primary-foreground text-sm font-black tracking-wider sm:text-lg"
            >
              {fmtTime(timer)}
            </m.span>
          ) : (
            <span className="text-background text-sm font-black tracking-wider sm:text-lg">
              {fmtTime(timer)}
            </span>
          )}
        </m.div>
        <Button type="button" size="lg" onClick={() => setShowSubmitConfirm(true)}>
          <Send size={13} />
          <span className="hidden sm:inline">Kumpulkan</span>
        </Button>
      </header>

      <div className="flex min-h-0 flex-1">
        <main ref={demoMainRef} className="flex flex-1 flex-col overflow-y-auto">
          <div className="flex-1">
            <div
              key={question.id}
              className="mx-auto w-full max-w-2xl px-5 py-6 sm:px-8 sm:py-10"
            >
              {DEMO_SECTIONS.length > 1 && (
                <div className="mb-4 flex items-center gap-1.5 sm:hidden">
                  <span className="bg-primary text-primary-foreground text-2xs rounded-lg px-2.5 py-1 font-bold">
                    {section.title}
                  </span>
                  <span className="text-foreground/30 text-2xs">
                    {sectionIdx + 1} / {DEMO_SECTIONS.length}
                  </span>
                </div>
              )}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-foreground/40 text-xs font-medium">
                    Soal {questionIdx + 1} dari {section.questions.length}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-foreground text-lg font-black">
                      Soal {globalIdx}
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant={flags.has(question.id) ? "secondary" : "outline"}
                  size="sm"
                  onClick={toggleFlag}
                >
                  <Flag
                    size={13}
                    fill={flags.has(question.id) ? "currentColor" : "none"}
                  />{" "}
                  Ragu
                </Button>
              </div>
              <p className="text-foreground mt-6 text-base leading-[1.75]">
                {question.text}
              </p>
              {question.image && (
                <Image
                  src={question.image}
                  alt="Soal"
                  width={400}
                  height={200}
                  sizes="(max-width: 768px) 100vw, 500px"
                  className="border-foreground/[0.08] bg-card my-4 max-h-[200px] max-w-full rounded-xl border object-contain p-2"
                />
              )}
              <div className="mt-6 flex flex-col gap-2.5">
                {question.options.map((opt) => {
                  const isSelected = answers[question.id] === opt.key;
                  return (
                    <Button
                      key={opt.key}
                      type="button"
                      variant={isSelected ? "default" : "outline"}
                      size="xl"
                      onClick={() => selectOption(opt.key)}
                      className="h-auto w-full items-start gap-4 px-5 py-4 text-left"
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold transition-all ${isSelected ? "bg-card text-primary scale-110" : "bg-foreground/[0.06] text-foreground/40"}`}
                      >
                        {opt.key}
                      </span>
                      <div className="flex flex-col gap-2 pt-1">
                        {opt.image && (
                          <Image
                            src={opt.image}
                            alt={`Opsi ${opt.key}`}
                            width={64}
                            height={64}
                            className="border-foreground/[0.08] bg-card h-16 w-16 rounded-lg border object-contain p-1 sm:h-20 sm:w-20"
                          />
                        )}
                        {opt.text && (
                          <span
                            className={`text-[15px] leading-relaxed ${isSelected ? "text-primary-foreground font-medium" : "text-foreground/70"}`}
                          >
                            {opt.text}
                          </span>
                        )}
                      </div>
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="border-border/60 bg-card/95 sticky bottom-0 flex shrink-0 items-center justify-between border-t px-4 py-3 backdrop-blur-sm sm:px-6 sm:py-4">
            <Button
              type="button"
              variant="outline"
              size="xl"
              onClick={goPrev}
              disabled={isFirst}
            >
              <ChevronLeft size={18} />
              <span className="hidden sm:inline">Sebelumnya</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => setShowNav(true)}
              className="sm:hidden"
            >
              <Grid3X3 size={15} /> {answeredCount}/{totalQuestions}
            </Button>
            <Button
              type="button"
              size="xl"
              onClick={isLast ? () => setShowSubmitConfirm(true) : goNext}
            >
              <span>{isLast ? "Kumpulkan" : "Selanjutnya"}</span>
              <ChevronRight size={18} />
            </Button>
          </div>
        </main>

        {/* Desktop sidebar */}
        <aside className="border-border/60 bg-card hidden w-64 shrink-0 flex-col border-l sm:flex">
          <div className="flex-1 overflow-y-auto p-5">
            <p className="text-foreground/30 mb-4 text-xs font-black tracking-wider uppercase">
              Navigasi Soal
            </p>
            {DEMO_SECTIONS.map((sec, si) => (
              <div key={sec.id} className="mb-5 last:mb-0">
                <p className="text-foreground/60 text-2xs mb-2 font-bold tracking-wider uppercase">
                  {sec.title}{" "}
                  <span className="font-normal">({sec.questions.length})</span>
                </p>
                <div className="grid grid-cols-5 gap-1.5">
                  {sec.questions.map((q, qi) => {
                    const num = getGlobalIdx(si, qi);
                    const isCurrent = si === sectionIdx && qi === questionIdx;
                    const isAnswered = !!answers[q.id];
                    const isFlagged = flags.has(q.id);
                    return (
                      <Button
                        key={q.id}
                        type="button"
                        variant={
                          isCurrent ? "default" : isAnswered ? "secondary" : "outline"
                        }
                        size="icon-lg"
                        onClick={() => goTo(si, qi)}
                        className="relative"
                      >
                        {num}
                        {isFlagged && (
                          <span className="bg-secondary absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white" />
                        )}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="border-border/60 border-t-2 px-5 py-3">
            <div className="text-foreground/40 text-3xs grid grid-cols-2 gap-y-2 font-bold">
              <span className="flex items-center gap-1.5">
                <span className="bg-primary shadow-soft-sm h-3 w-3 rounded-md [--soft-shadow-color:var(--primary)]" />{" "}
                Aktif
              </span>
              <span className="flex items-center gap-1.5">
                <span className="bg-secondary h-3 w-3 rounded-md" /> Dijawab
              </span>
              <span className="flex items-center gap-1.5">
                <span className="border-foreground/[0.1] h-3 w-3 rounded-md border-2" />{" "}
                Belum
              </span>
              <span className="flex items-center gap-1.5">
                <span className="border-foreground/[0.1] relative h-3 w-3 rounded-md border-2">
                  <span className="bg-secondary absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full border border-white" />
                </span>{" "}
                Ragu
              </span>
            </div>
          </div>
        </aside>
      </div>

      {/* Mobile nav sheet */}
      {showNav && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => setShowNav(false)}
          />
          <div className="bg-card fixed inset-x-0 bottom-0 z-50 max-h-[70vh] overflow-y-auto rounded-t-2xl px-5 pt-3 pb-8">
            <div className="mb-4 flex items-center justify-between">
              <div className="bg-foreground/10 absolute top-2 left-1/2 h-1 w-10 -translate-x-1/2 rounded-full" />
              <p className="text-foreground text-sm font-bold">Navigasi Soal</p>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => setShowNav(false)}
              >
                <X size={16} />
              </Button>
            </div>
            {DEMO_SECTIONS.map((sec, si) => (
              <div key={sec.id} className="mb-5 last:mb-0">
                <p className="text-foreground/60 text-2xs mb-2 font-bold tracking-wider uppercase">
                  {sec.title}{" "}
                  <span className="font-normal">({sec.questions.length})</span>
                </p>
                <div className="grid grid-cols-7 gap-1.5">
                  {sec.questions.map((q, qi) => {
                    const num = getGlobalIdx(si, qi);
                    const isCurrent = si === sectionIdx && qi === questionIdx;
                    const isAnswered = !!answers[q.id];
                    const isFlagged = flags.has(q.id);
                    return (
                      <Button
                        key={q.id}
                        type="button"
                        variant={
                          isCurrent ? "default" : isAnswered ? "secondary" : "outline"
                        }
                        size="icon-xl"
                        onClick={() => goTo(si, qi)}
                        className="relative"
                      >
                        {num}
                        {isFlagged && (
                          <span className="bg-secondary absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white" />
                        )}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Tab warning modal */}
      {tabWarning && (
        <DemoModal onModalClose={() => setTabWarning(false)}>
          <div className="flex items-center gap-3">
            <div className="bg-destructive/10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full">
              <AlertTriangle size={22} className="text-destructive" />
            </div>
            <div>
              <h3 className="text-foreground text-base font-bold">Peringatan!</h3>
              <p className="text-foreground/60 text-sm">Kamu terdeteksi berpindah tab.</p>
            </div>
          </div>
          <p className="text-foreground/60 mt-3 text-sm">
            Waktu ujian dikurangi <strong className="text-destructive">10 detik</strong>{" "}
            sebagai penalti.
          </p>
          <p className="text-destructive mt-2 text-xs font-medium">
            Pelanggaran ke-{tabSwitchCount} &middot; Total penalti: -{tabSwitchCount * 10}{" "}
            detik
          </p>
          <Button
            type="button"
            size="xl"
            onClick={() => setTabWarning(false)}
            className="mt-5 w-full"
          >
            Saya mengerti
          </Button>
        </DemoModal>
      )}

      {/* Fullscreen warning modal */}
      {showFullscreenWarning && (
        <DemoModal>
          <div className="flex items-center gap-3">
            <div className="bg-secondary/50 flex h-11 w-11 shrink-0 items-center justify-center rounded-full">
              <AlertTriangle size={22} className="text-secondary-foreground" />
            </div>
            <div>
              <h3 className="text-foreground text-base font-bold">
                Layar Penuh Dinonaktifkan
              </h3>
            </div>
          </div>
          <p className="text-foreground/60 mt-3 text-sm">
            Silakan kembali ke layar penuh untuk melanjutkan.
          </p>
          <Button
            type="button"
            size="xl"
            onClick={() => {
              setShowFullscreenWarning(false);
              requestFullscreen();
            }}
            className="mt-5 w-full"
          >
            Kembali ke Layar Penuh
          </Button>
        </DemoModal>
      )}

      {/* Submit confirm modal */}
      {showSubmitConfirm && (
        <DemoModal onModalClose={() => setShowSubmitConfirm(false)}>
          <h3 className="text-foreground text-base font-black">Kumpulkan Jawaban?</h3>
          <p className="text-foreground/60 mt-2 text-sm">
            Setelah dikumpulkan, jawaban tidak dapat diubah lagi.
          </p>
          <div className="border-foreground/[0.08] divide-foreground/[0.06] mt-4 divide-y rounded-lg border">
            {DEMO_SECTIONS.map((sec) => {
              const answered = sec.questions.filter((q) => answers[q.id]).length;
              const flagged = sec.questions.filter((q) => flags.has(q.id)).length;
              return (
                <div
                  key={sec.id}
                  className="flex items-center justify-between px-4 py-2.5"
                >
                  <span className="text-foreground/70 text-sm">{sec.title}</span>
                  <span className="text-foreground/50 text-xs">
                    {answered}/{sec.questions.length}
                    {flagged > 0 && (
                      <span className="text-secondary ml-1">({flagged} ragu)</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          {(() => {
            const u = totalQuestions - answeredCount;
            return u > 0 ? (
              <p className="text-destructive mt-3 text-xs font-medium">
                {u} soal masih belum dijawab
              </p>
            ) : null;
          })()}
          <div className="mt-5 flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => setShowSubmitConfirm(false)}
              className="flex-1"
            >
              Kembali
            </Button>
            <Button type="button" size="lg" onClick={handleSubmit} className="flex-1">
              Kumpulkan
            </Button>
          </div>
        </DemoModal>
      )}
    </div>
  );
}
